import { useEffect, useState } from "react";

import classNames from "../../utils/classNames";

// Inline cell editors for the Waste Tracking table. The shared Inputs are built around
// react-hook-form's register, which doesn't fit one-input-per-cell editing, so these are the
// same Tailwind shapes wired to local state and a commit-on-blur callback. Styling matches
// YardStatusDropdown, the other inline cell editor in the app.

export const cellInputClasses = "w-full border border-gray-300 rounded-md p-1 text-sm text-gray-900 disabled:bg-gray-100 disabled:text-gray-500";

interface TextCellProps {
  value: string | null | undefined;
  onCommit: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  upperCase?: boolean;
  invalid?: boolean;
  width?: string;
  type?: "text" | "number" | "datetime-local";
  step?: string;
}

export function WasteReceiptTextCell({
  value,
  onCommit,
  disabled = false,
  placeholder = "",
  upperCase = false,
  invalid = false,
  width = "w-36",
  type = "text",
  step,
}: TextCellProps) {
  const [draft, setDraft] = useState(value ?? "");

  // Server refresh (bulk update, resubmission) wins over a stale local copy.
  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const commit = () => {
    const next = draft.trim();
    const current = (value ?? "").trim();
    if (next === current) return;
    onCommit(next.length === 0 ? null : upperCase ? next.toUpperCase() : next);
  };

  return (
    <div className={width} onClick={(e) => e.stopPropagation()}>
      <input
        type={type}
        step={step}
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setDraft(value ?? "");
        }}
        className={classNames(cellInputClasses, upperCase ? "uppercase" : "", invalid ? "border-red-400 bg-red-50" : "")}
      />
    </div>
  );
}

interface SelectCellProps<T extends string | number> {
  value: T | null | undefined;
  options: { value: T; label: string }[];
  onCommit: (value: T) => void;
  disabled?: boolean;
  width?: string;
}

export function WasteReceiptSelectCell<T extends string | number>({ value, options, onCommit, disabled = false, width = "w-44" }: SelectCellProps<T>) {
  return (
    <div className={width} onClick={(e) => e.stopPropagation()}>
      <select
        value={value === null || value === undefined ? "" : String(value)}
        disabled={disabled}
        onChange={(e) => {
          const picked = options.find((o) => String(o.value) === e.target.value);
          if (picked) onCommit(picked.value);
        }}
        className={cellInputClasses}
      >
        {(value === null || value === undefined) && <option value="">Select…</option>}
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface CheckboxCellProps {
  checked: boolean;
  onCommit: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
}

export function WasteReceiptCheckboxCell({ checked, onCommit, disabled = false, label }: CheckboxCellProps) {
  return (
    <label className="flex items-center gap-1 text-xs text-gray-600" onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCommit(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
      />
      {label}
    </label>
  );
}
